import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Users, 
  Plus, 
  Search, 
  UserPlus, 
  Crown, 
  Edit, 
  Eye, 
  MessageSquare,
  Trash2,
  Mail,
  Calendar,
  Activity,
  GitBranch,
  GitPullRequest,
  GitFork,
  X,
  BarChart3
} from 'lucide-react';
import { 
  getCollaborators, 
  addCollaborator, 
  updateCollaborator, 
  removeCollaborator,
  searchUsers,
  getCollaboratorActivity,
  getPrompt 
} from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import PromptTabs from '../components/PromptTabs';

const PromptCollaborators = ({ user }) => {
  const { promptId } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({
    email: '',
    permissionLevel: 'commenter',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async () => {
    try {
      const [promptData, collaboratorsData] = await Promise.all([
        getPrompt(promptId),
        getCollaborators(promptId, user.id)
      ]);
      setPrompt(promptData);
      setCollaborators(collaboratorsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const activityData = await getCollaboratorActivity(promptId, user.id);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const handleSearchUsers = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const results = await searchUsers(query, promptId, user.id);
      setSearchResults(results);
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleInviteCollaborator = async () => {
    if (!newCollaborator.email.trim()) return;

    try {
      await addCollaborator({
        promptId,
        userEmail: newCollaborator.email,
        permissionLevel: newCollaborator.permissionLevel,
        invitedBy: user.id,
        message: newCollaborator.message || ''
      });
      
      setShowInviteModal(false);
      setNewCollaborator({ email: '', permissionLevel: 'commenter', message: '' });
      setSearchQuery('');
      setSearchResults([]);
      alert('Collaboration invitation sent successfully!');
    } catch (error) {
      console.error('Failed to invite collaborator:', error);
      alert('Failed to send invitation. Please try again.');
    }
  };

  const handleUpdatePermission = async (collaboratorId, newPermission) => {
    try {
      await updateCollaborator(collaboratorId, {
        permissionLevel: newPermission,
        userId: user.id
      });
      loadData();
    } catch (error) {
      console.error('Failed to update permission:', error);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId, collaboratorName) => {
    if (!window.confirm(`Remove ${collaboratorName} from this prompt?`)) {
      return;
    }

    try {
      await removeCollaborator(collaboratorId, user.id);
      loadData();
    } catch (error) {
      console.error('Failed to remove collaborator:', error);
    }
  };

  const getPermissionIcon = (level) => {
    switch (level) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'editor':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'reviewer':
        return <Eye className="h-4 w-4 text-green-600" />;
      case 'commenter':
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
      default:
        return <Users className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'reviewer':
        return 'bg-green-100 text-green-800';
      case 'commenter':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'version_created':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'pr_created':
        return <UserPlus className="h-4 w-4 text-green-600" />;
      case 'comment_added':
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
      case 'review_approve':
        return <Eye className="h-4 w-4 text-green-600" />;
      case 'review_request_changes':
        return <Eye className="h-4 w-4 text-red-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">Collaborators</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Collaborators</h1>
          <p className="text-gray-600 mt-2">
            Manage who can access and contribute to {prompt.name}
          </p>
          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              {collaborators.length} collaborator{collaborators.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => {
              loadActivity();
              setShowActivityModal(true);
            }}
            className="btn-secondary"
          >
            <Activity className="h-4 w-4 mr-2" />
            Activity
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Invite Collaborator
          </button>
        </div>
      </div>

      {/* Collaboration Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      {/* Collaborators List */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        </div>
        
        {collaborators.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No collaborators yet</h3>
            <p className="text-gray-600 mb-4">
              Invite team members to collaborate on this prompt
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-primary"
            >
              Invite your first collaborator
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {collaborators.map((collaborator) => (
              <div key={collaborator.id} className="p-6">
                <CollaboratorCard 
                  collaborator={collaborator}
                  currentUser={user}
                  onUpdatePermission={handleUpdatePermission}
                  onRemove={handleRemoveCollaborator}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Collaborator</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="collaboratorSearch" className="block text-sm font-medium text-gray-700 mb-1">
                  Find User
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    id="collaboratorSearch"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleSearchUsers(e.target.value);
                    }}
                    className="input pl-10"
                    placeholder="Search by name or email..."
                  />
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setNewCollaborator({ ...newCollaborator, email: user.email });
                          setSearchQuery(user.full_name);
                          setSearchResults([]);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {searching && (
                  <div className="mt-2 text-sm text-gray-500">Searching...</div>
                )}
              </div>
              
              <div>
                <label htmlFor="collaboratorEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="collaboratorEmail"
                  value={newCollaborator.email}
                  onChange={(e) => setNewCollaborator({ ...newCollaborator, email: e.target.value })}
                  className="input"
                  placeholder="user@example.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="permissionLevel" className="block text-sm font-medium text-gray-700 mb-1">
                  Permission Level
                </label>
                <select
                  id="permissionLevel"
                  value={newCollaborator.permissionLevel}
                  onChange={(e) => setNewCollaborator({ ...newCollaborator, permissionLevel: e.target.value })}
                  className="input"
                >
                  <option value="commenter">Commenter - Can view and comment</option>
                  <option value="reviewer">Reviewer - Can review pull requests</option>
                  <option value="editor">Editor - Can create branches and PRs</option>
                  <option value="owner">Owner - Full access and management</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700 mb-1">
                  Message (Optional)
                </label>
                <textarea
                  id="inviteMessage"
                  value={newCollaborator.message}
                  onChange={(e) => setNewCollaborator({ ...newCollaborator, message: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Add a personal message to your invitation..."
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleInviteCollaborator}
                className="btn-primary"
                disabled={!newCollaborator.email.trim()}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Send Invitation
              </button>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setNewCollaborator({ email: '', permissionLevel: 'commenter', message: '' });
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <button
                onClick={() => setShowActivityModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {activity.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No recent activity</p>
                </div>
              ) : (
                activity.map((item, index) => (
                  <div key={index} className="flex space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      {getActivityIcon(item.activity_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="font-medium">{item.full_name}</span>
                        <span className="text-gray-500">
                          {item.activity_type.replace('_', ' ')}
                        </span>
                        <span className="text-gray-400">
                          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      {item.description && (
                        <div className="mt-1 text-sm text-gray-700 line-clamp-2">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CollaboratorCard = ({ collaborator, currentUser, onUpdatePermission, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [newPermission, setNewPermission] = useState(collaborator.permission_level);

  const handleSavePermission = () => {
    if (newPermission !== collaborator.permission_level) {
      onUpdatePermission(collaborator.id, newPermission);
    }
    setEditing(false);
  };

  const getPermissionIcon = (level) => {
    switch (level) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'editor':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'reviewer':
        return <Eye className="h-4 w-4 text-green-600" />;
      case 'commenter':
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
      default:
        return <Users className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'reviewer':
        return 'bg-green-100 text-green-800';
      case 'commenter':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canEdit = currentUser.id !== collaborator.user_id; // Can't edit own permissions

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-primary-600">
            {collaborator.full_name.charAt(0).toUpperCase()}
          </span>
        </div>
        
        <div>
          <div className="flex items-center space-x-2">
            <h4 className="font-medium text-gray-900">{collaborator.full_name}</h4>
            {currentUser.id === collaborator.user_id && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">You</span>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Mail className="h-3 w-3" />
            <span>{collaborator.email}</span>
          </div>
          {collaborator.invited_by_name && (
            <div className="flex items-center space-x-1 text-xs text-gray-400 mt-1">
              <span>Invited by {collaborator.invited_by_name}</span>
              <Calendar className="h-3 w-3 ml-1" />
              <span>{formatDistanceToNow(new Date(collaborator.created_at), { addSuffix: true })}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-3">
        {editing ? (
          <div className="flex items-center space-x-2">
            <select
              value={newPermission}
              onChange={(e) => setNewPermission(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="commenter">Commenter</option>
              <option value="reviewer">Reviewer</option>
              <option value="editor">Editor</option>
              <option value="owner">Owner</option>
            </select>
            <button
              onClick={handleSavePermission}
              className="text-sm bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700"
            >
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setNewPermission(collaborator.permission_level);
              }}
              className="text-sm bg-gray-300 text-gray-700 px-2 py-1 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(collaborator.permission_level)}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPermissionColor(collaborator.permission_level)}`}>
                {collaborator.permission_level.charAt(0).toUpperCase() + collaborator.permission_level.slice(1)}
              </span>
            </div>
            
            {canEdit && (
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setEditing(true)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Edit permissions"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onRemove(collaborator.id, collaborator.full_name)}
                  className="text-gray-400 hover:text-red-600"
                  title="Remove collaborator"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromptCollaborators; 